<template>
	<div>
		<div class="flex flex-1 items-center mt-2 gap-2">
			<slot name="searchFilter">
				<app-input
					v-if="searchable"
					v-model="computedSearch"
					name="search"
					placeholder="Search"
					:icon="searching ? 'spin-loading' : 'search'"
					class="w-full"
					@update:modelValue="_onSearchInput"
				/>
			</slot>
			<slot name="columnsFilter">
				<table-columns-filter
					v-if="columns.some((column) => column.isSelected)"
					:columns="columns"
					@update:selectedColumns="updateSelectedColumns($event)"
				/>
			</slot>
			<slot name="customOptions"></slot>
		</div>
		<table v-if="onTableColumns.length" class="app-table" :class="$attrs.class ? $attrs.class : ''" v-bind="$attrs">
			<thead class="sticky-top">
				<tr class="app-table-head-row">
					<th v-for="column in onTableColumns" :key="column.key" :class="column.class">
						<div v-if="!column.hidden" class="flex">
							<div class="flex gap-1" v-if="withSelectAll && column.key === 'selectAll'">
								<input
									:id="'selectAll-' + tableId"
									type="checkbox"
									v-model="selectAll"
									@change="$emit('selectAllRows', selectAll)"
								/>
								<label :for="'selectAll-' + tableId" class="flex flex-1 self-center">{{
									column.label
								}}</label>
							</div>
							<p v-else :class="`flex flex-1 ${chooseHeaderClass(column)}`">
								{{ column.label }}
							</p>
							<p class="flex flex-col" v-if="column.isSortable">
								<app-icon
									:class="
										sortByDirection === 'asc' && sortBy === column.key
											? 'text-primary'
											: 'text-secondary'
									"
									class="float-right self-center h-8px cursor-pointer"
									icon="sort-up"
									@click="fireSortBy(column.key, 'asc')"
								/>
								<app-icon
									:class="
										sortByDirection !== 'asc' && sortBy === column.key
											? 'text-primary'
											: 'text-secondary'
									"
									class="float-right self-center h-8px cursor-pointer"
									icon="sort-down"
									@click="fireSortBy(column.key, 'desc')"
								/>
							</p>
						</div>
					</th>
					<th class="actions" v-if="$slots.actions">{{ actionsColName }}</th>
				</tr>
			</thead>
			<tbody class="overflow-y-auto">
				<template v-for="(item, index) in tableData" :key="index">
					<tr
						:class="[
							'app-table-body-row',
							{ 'bg-gray-300 hover:bg-gray-400': item[trackBy] && item[trackBy] === selectedItemValue },
							{
								'bg-gray-300 hover:bg-gray-400':
									selectedItemsValue && selectedItemsValue.includes(item),
							},
							{ 'cursor-pointer': expandable },
							{ 'sticky-bottom': stickyBottom && isLastRow(index) },
						]"
						@click="onRowClick(item, index)"
					>
						<td
							v-for="column in onTableColumns"
							:class="[chooseClass(item, column)]"
							:key="`${index}-${column.key}`"
						>
							<div v-if="!column.hidden">
								<span v-if="!$slots[column.key]">{{
									column.key === 'city' ? item[column.key].description : item[column.key]
								}}</span>
								<span v-if="$slots[column.key]">
									<slot :name="column.key" v-bind:item="item" v-bind:index="index" />
								</span>
							</div>
						</td>
						<td class="actions" v-if="$slots.actions">
							<slot name="actions" v-bind:item="item" v-bind:index="index" />
						</td>
					</tr>
					<transition name="slide-down" mode="out-in">
						<tr
							class="app-table-body-row no-hover"
							v-if="shouldShowDetailsRow[index] || openedIndex === index"
						>
							<td :colspan="onTableColumns.length">
								<slot name="appDetails" v-bind:item="item" v-bind:index="index" />
							</td>
						</tr>
					</transition>
				</template>
			</tbody>
		</table>
		<div v-else class="flex justify-center items-center gap-2 h-32 border-1 border-gray-300">
			<app-icon icon="columns" class="text-gray-600 text-2xl" />
			<div class="text-gray-600 text-lg">No Columns Selected</div>
		</div>
	</div>
</template>

<script>
	import AppIcon from '@/components/app/AppIcon';
	import UtilityService from '@/services/UtilityService';
	import { debounce } from 'debounce';
	import TableColumnsFilter from '@/components/app/TableColumnsFilter.vue';

	export default {
		name: 'AppTable',
		emits: ['rowClick', 'sortBy', 'onSearch', 'selectAllRows'],
		props: {
			columns: {
				type: Array,
				required: true,
			},
			data: {
				type: Array,
				required: true,
			},
			loading: {
				type: Boolean,
				required: false,
			},
			selectedItemValue: {
				type: [Number, Object],
				required: false,
			},
			selectedItemsValue: {
				type: Array,
				required: false,
			},
			trackBy: {
				type: String,
				default: '',
			},
			stickyBottom: {
				type: Boolean,
				default: false,
			},
			actionsColName: {
				type: String,
				default: 'Actions',
			},
			searchable: {
				required: false,
				default: true,
			},
			remoteSearchable: {
				required: false,
				default: false,
			},
			searching: {
				required: false,
				default: false,
			},
			expandable: {
				required: false,
				default: false,
			},
			oneAtATime: {
				required: false,
				default: false,
			},
			withSelectAll: {
				type: Boolean,
				default: false,
			},
		},
		components: {
			TableColumnsFilter,
			AppIcon,
		},
		computed: {
			computedSearch: {
				get() {
					return this.search;
				},
				set: debounce(function(newValue) {
					this.search = newValue;
				}, 100),
			},
			tableData() {
				const column = this.onTableColumns.find((c) => {
					return c.key === this.sortBy;
				});
				if (!column && this.search && !this.remoteSearchable) {
					return [...this.data].filter((item) =>
						UtilityService.dynamicFilter(item, this.onTableColumns, this.computedSearch.toLowerCase())
					);
				}
				return [...this.data]
					.filter((item) =>
						UtilityService.dynamicFilter(
							item,
							this.onTableColumns,
							this.remoteSearchable ? '' : this.computedSearch.toLowerCase()
						)
					)
					.sort((a, b) => {
						return UtilityService.dynamicSortBy(
							a,
							b,
							column && column.keyProp ? column.keyProp : this.sortBy,
							this.sortByDirection
						);
					});
			},
		},
		data: () => ({
			sortBy: '',
			sortByDirection: '',
			search: '',
			shouldShowDetailsRow: {},
			openedIndex: -1,
			selectAll: false,
			onTableColumns: [],
			tableId: Math.random()
				.toString(36)
				.substr(2, 9),
		}),
		methods: {
			_onSearchInput: debounce(function(v) {
				this.$emit('onSearch', v);
			}, 300),
			isLastRow(index) {
				return index === this.tableData.length - 1;
			},
			fireSortBy(prop, direction) {
				this.sortBy = prop;
				this.sortByDirection = direction;
				this.$emit('sortBy', { prop, direction });
			},
			updateSelectedColumns(selectedColumns) {
				this.onTableColumns = this.columns.filter((column) => selectedColumns.includes(column.key));
			},
			onRowClick(item, index) {
				if (this.expandable) {
					if (this.oneAtATime) {
						if (this.openedIndex === index) {
							this.openedIndex = -1;
						} else {
							this.openedIndex = index;
						}
					} else {
						this.shouldShowDetailsRow[index] = !this.shouldShowDetailsRow[index];
					}
				}
				this.$emit('rowClick', item);
			},
			chooseHeaderClass(column) {
				if (
					[
						'Balance',
						'Budget Spent',
						'Orders No',
						'Employees No',
						'Daily Budget',
						'Company Daily Budget',
						'Employee Daily Budget',
						'Total Daily Budget',
						'Pre Balance',
						'Total Budget',
						'Budget',
						'Total',
						'Paid',
						'Unpaid',
						'Delivery Time',
						'Total Orders',
						'B2B Balance',
						'Transport Cost',
						'Hours',
						'Amount',
						'Total Employees',
						'Days No',
						'Progressive',
						'Total Quantity',
						'Total Value',
						'Avg Price',
						'Growth %',
						'Exchange Rate',
						'Value ALL',
						'Transport',
						'Commission',
						'Expire Minutes',
						'Amount with VAT',
						'VAT',
						'Price',
						'Quantity',
						'Has Variables',
						'Acc. ID',
						'Spent',
						'Tolerance',
						'Paid Amount',
						'Order No',
						'Min Orders No',
						'Max Orders No',
						'Min Employees No',
						'Max Employees No',
					].includes(column.label)
				) {
					return 'text-right justify-end';
				}
			},
			chooseClass(item, column) {
				if (
					(typeof item[column.key] === 'number' &&
						![
							'Id',
							'ID',
							'Type',
							'Priority',
							'Tot Users',
							'Channel',
							'GoalType',
							'Status',
							'Doc Type',
							'Doc ID',
						].includes(column.label)) ||
					['Avg Price', 'Growth %', 'Value ALL', 'Adjustment'].includes(column.label)
				) {
					return 'text-right';
				} else return 'text-left';
			},
		},
		created() {
			const selectedColumns = this.columns.filter((column) => column.isSelected);
			this.onTableColumns = selectedColumns.length ? selectedColumns : this.columns;
		},
		mounted() {
			let defaultSortIndex = this.onTableColumns.findIndex((c) => c.initialSort);
			if (defaultSortIndex === -1) {
				return;
			}
			this.sortBy = this.onTableColumns[defaultSortIndex].key;
			this.sortByDirection = this.onTableColumns[defaultSortIndex].initialSortDirection
				? this.onTableColumns[defaultSortIndex].initialSortDirection
				: 'desc';
		},
		watch: {
			columns: {
				handler() {
					const selectedColumns = this.columns.filter((column) => column.isSelected);
					this.onTableColumns = selectedColumns.length ? selectedColumns : this.columns;
				},
			},
		},
	};
</script>

<style scoped>
	.sticky-bottom {
		position: sticky;
		bottom: 0;
		border-bottom: none !important;
		background-color: #f6f6f6;
		z-index: 5;
	}
	.sticky-bottom:hover {
		background-color: #efefef;
	}

	.sticky-top {
		position: sticky;
		top: 0;
		background-color: white;
		z-index: 1;
	}
</style>
